import React, { useState } from 'react';
import { ChevronDown, ShoppingCart, Truck, RotateCcw } from 'lucide-react';

/**
 * FAQAccordion - Частые вопросы с аккордеоном
 *
 * Features:
 * - 4 ключевых вопроса для главной страницы
 * - Glassmorphism design matching brand (#0EA5E9)
 * - Smooth expand/collapse animations
 * - Icons from Lucide React
 * - Adaptive layout (mobile/desktop)
 * - Adaptive padding synchronized with Benefits Widget (Tailwind classes)
 */

const FAQ_DATA = [
  {
    id: 'how-it-works',
    icon: ShoppingCart,
    question: 'Как это работает?',
    answer: (
      <p>
        Выбираете товары в каталоге, оформляете заказ — мы закупаем напрямую у поставщиков и доставляем к вашему подъезду.
        Заказы собираются в сборы по 5-7 дней, поэтому цены ниже, чем в магазинах.
      </p>
    ),
    priority: 1
  },
  {
    id: 'delivery',
    icon: Truck,
    question: 'Как и когда доставка?',
    answer: (
      <p>
        Доставка в конце каждого сбора — обычно через 5-7 дней после заказа.
        Привозим в термобоксах к подъезду, предупредим заранее о точном времени.
      </p>
    ),
    priority: 2
  },
  {
    id: 'return',
    icon: RotateCcw,
    question: 'Что если не понравится?',
    answer: (
      <p>
        Вернём деньги. Можно отменить заказ до начала закупки, а если товар не устроит по качеству — оформим возврат.
      </p>
    ),
    priority: 3
  }
];

const FAQAccordion = ({ maxItems = 4, showMoreLink = false }) => {
  const [openId, setOpenId] = useState(null);

  const toggleItem = (id) => {
    setOpenId(openId === id ? null : id);
  };

  const displayedFAQs = FAQ_DATA.slice(0, maxItems);

  return (
    <div className="faq-section mb-8 px-4 md:px-6 lg:px-10">
      <div className="max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Частые вопросы
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Коротко о главном
          </p>
        </div>

        {/* Accordion Items */}
        <div className="space-y-3">
          {displayedFAQs.map((faq) => (
            <FAQItem
              key={faq.id}
              faq={faq}
              isOpen={openId === faq.id}
              onToggle={() => toggleItem(faq.id)}
            />
          ))}
        </div>

        {/* Show More Link */}
        {showMoreLink && (
          <div className="mt-6">
            <a
              href="/faq"
              className="inline-flex items-center gap-2 text-cyan-600 dark:text-cyan-400 font-medium hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors"
            >
              Смотреть все вопросы
              <ChevronDown className="w-4 h-4 -rotate-90" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * FAQItem - Single accordion item
 */
const FAQItem = ({ faq, isOpen, onToggle }) => {
  const Icon = faq.icon;

  return (
    <div
      className={`faq-item bg-gradient-to-br from-white/90 to-white/70 dark:from-gray-800/90 dark:to-gray-800/70 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg overflow-hidden transition-all duration-300 ${
        isOpen ? 'shadow-xl shadow-cyan-500/10' : 'hover:shadow-xl'
      }`}
    >
      {/* Question Header (clickable) */}
      <button
        id={`faq-header-${faq.id}`}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-panel-${faq.id}`}
        className="w-full flex items-start gap-4 p-4 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 rounded-2xl transition-all"
      >
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
            isOpen
              ? 'bg-gradient-to-br from-cyan-500 to-blue-500 shadow-lg shadow-cyan-500/30'
              : 'bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/30 dark:to-blue-900/30'
          }`}
        >
          <Icon
            className={`w-5 h-5 transition-colors ${
              isOpen ? 'text-white' : 'text-cyan-600 dark:text-cyan-400'
            }`}
            strokeWidth={2}
          />
        </div>

        {/* Question Text */}
        <div className="flex-1 min-w-0">
          <h3
            className={`font-semibold transition-colors ${
              isOpen
                ? 'text-cyan-700 dark:text-cyan-300'
                : 'text-gray-900 dark:text-white'
            }`}
          >
            {faq.question}
          </h3>
        </div>

        {/* Chevron */}
        <div className="flex-shrink-0">
          <ChevronDown
            className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
              isOpen ? 'rotate-180 text-cyan-600 dark:text-cyan-400' : ''
            }`}
            strokeWidth={2}
          />
        </div>
      </button>

      {/* Answer (expandable) */}
      <div
        id={`faq-panel-${faq.id}`}
        role="region"
        aria-labelledby={`faq-header-${faq.id}`}
        aria-hidden={!isOpen}
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pb-4 pt-0 pl-14">
          <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            {faq.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQAccordion;
